import { useCallback, useEffect, useSyncExternalStore } from "react";
import { demoApi } from "@/api/client";
import type { HomeLayout, HomeTileRef } from "@/api/types";

/**
 * The caller's Home layout — ONE shared state, so the dialog's edits show on Home behind it at once.
 *
 * MODULE-SCOPED STORE (the way `useSelectedEntity` shares state, but through useSyncExternalStore): every
 * mounted instance reads the same snapshot, loaded once on first use. `save`/`reset` resolve once the server
 * answered and the store holds the returned layout; they reject — leaving the store untouched — when the
 * request fails, and `error` then carries a plain-language message. A failed initial READ sets
 * `status: "error"` with `error` set (Home then shows the default set, see Dashboard.tsx).
 * Interface is FROZEN (ef-061 anchor). See `ef-061-4-build.md`.
 */
export interface HomeLayoutState {
  status: "loading" | "ready" | "error";
  /** The tiles to render, in order (permission-filtered by the server). Empty while loading. */
  tiles: HomeTileRef[];
  /** Permitted tiles not currently on Home — the picker's "add" list. */
  available: HomeTileRef[];
  isDefault: boolean;
  error: string | null;
  /** True while a save/reset is in flight. */
  saving: boolean;
  /** Save the whole ordered list of tile ids (PUT). */
  save: (ids: string[]) => Promise<void>;
  /** Back to the default set (DELETE). */
  reset: () => Promise<void>;
}

type Snapshot = Omit<HomeLayoutState, "save" | "reset">;

let snapshot: Snapshot = {
  status: "loading",
  tiles: [],
  available: [],
  isDefault: true,
  error: null,
  saving: false,
};
let started = false;
const listeners = new Set<() => void>();

function publish(next: Partial<Snapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const listener of listeners) listener();
}

function applyLayout(layout: HomeLayout): void {
  publish({
    status: "ready",
    tiles: layout.tiles,
    available: layout.available,
    isDefault: layout.isDefault,
    error: null,
  });
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function ensureLoaded(): void {
  if (started) return;
  started = true;
  demoApi
    .getHomeLayout()
    .then(applyLayout)
    .catch((err: unknown) => {
      publish({ status: "error", tiles: [], available: [], error: message(err, "Couldn't load your layout.") });
    });
}

async function mutate(run: () => Promise<HomeLayout>, failure: string): Promise<void> {
  publish({ saving: true, error: null });
  try {
    const layout = await run();
    publish({ saving: false });
    applyLayout(layout);
  } catch (err) {
    publish({ saving: false, error: message(err, failure) });
    throw err;
  }
}

const save = (ids: string[]): Promise<void> =>
  mutate(() => demoApi.saveHomeLayout(ids), "Couldn't save your layout.");
const reset = (): Promise<void> =>
  mutate(() => demoApi.resetHomeLayout(), "Couldn't reset your layout.");

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): Snapshot => snapshot;

export function useHomeLayout(): HomeLayoutState {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(ensureLoaded, []);
  const stableSave = useCallback(save, []);
  const stableReset = useCallback(reset, []);
  return { ...state, save: stableSave, reset: stableReset };
}
